"use client";

import { ChartConfig, ChartContainer } from "@borderless/ui/components/chart";
import {
  Label,
  PolarGrid,
  PolarRadiusAxis,
  RadialBar,
  RadialBarChart,
} from "@borderless/ui/lib/recharts";
import { cn } from "@borderless/ui/lib/utils";

const chartConfig = {
  percentage: {
    label: "%",
  },
} satisfies ChartConfig;

export function PercentageRadialChart(props: {
  isNotAvailable?: boolean;
  data: {
    label: string;
    percentage: number;
  };
  significantDigits: number;
  centerText: string;
  className?: string;
  innerRadius?: number;
  outerRadius?: number;
  polarRadius?: [number, number];
  centerTextClassName?: string;
  labelClassName?: string;
  centerTextSpace?: number;
}) {
  const chartData = [
    {
      label: props.data.label,
      percentage: props.data.percentage,
      fill: "var(--color-primary)",
    },
  ];

  // Default values if not provided
  const innerRadius = props.innerRadius ?? 80;
  const outerRadius = props.outerRadius ?? 110;
  // Calculate polarRadius based on inner and outer radius if not provided
  const thickness = outerRadius - innerRadius;
  const border = 6;
  const polarRadius = props.polarRadius ?? [
    innerRadius + border,
    outerRadius - thickness - border,
  ];

  return (
    <ChartContainer
      config={chartConfig}
      className={cn("mx-auto aspect-square max-h-[200px]", props.className)}
    >
      <RadialBarChart
        data={chartData}
        startAngle={90}
        endAngle={90 - (360 * (props.data.percentage ?? 0)) / 100}
        innerRadius={innerRadius}
        outerRadius={outerRadius}
      >
        <PolarGrid
          gridType="circle"
          radialLines={false}
          stroke="none"
          className="first:fill-accent last:fill-background"
          polarRadius={polarRadius}
        />
        <RadialBar dataKey="percentage" cornerRadius={10} />
        <PolarRadiusAxis tick={false} tickLine={false} axisLine={false}>
          <Label
            content={({ viewBox }) => {
              if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                return (
                  <text
                    x={viewBox.cx}
                    y={viewBox.cy}
                    textAnchor="middle"
                    dominantBaseline="middle"
                  >
                    <tspan
                      x={viewBox.cx}
                      y={viewBox.cy}
                      className={cn(
                        "fill-foreground text-4xl font-bold",
                        props.isNotAvailable && "text-xl opacity-50",
                        props.centerTextClassName,
                      )}
                    >
                      {props.isNotAvailable
                        ? "Not available"
                        : `${props.data.percentage.toFixed(
                            props.significantDigits,
                          )}%`}
                    </tspan>
                    <tspan
                      x={viewBox.cx}
                      y={(viewBox.cy ?? 0) + (props.centerTextSpace ?? 24)}
                      className={cn(
                        "fill-foreground",
                        props.isNotAvailable && "text-xl opacity-50",
                        props.labelClassName,
                      )}
                    >
                      {props.centerText}
                    </tspan>
                  </text>
                );
              }
            }}
          />
        </PolarRadiusAxis>
      </RadialBarChart>
    </ChartContainer>
  );
}
